import {
  useContext,
  useEffect,
  useCallback,
  useReducer,
  useMemo,
  useRef
} from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { Store, TimeSlot, ValidStore } from '../types'
import { InvalidTimeSlot } from 'shop/components'
import {
  FulfillmentDateContainer,
  FulfillmentDatePicker
} from '../FulfillmentDatePicker'
import { LandingContextNew } from '../LandingContext'
import {
  dropdownIsEditable,
  fadeInOutMs,
  findSameDayAsapEnabledNoneAsapOnlyStore,
  findSameDayAsapEnabledStore,
  getInvalidStoresMessage,
  getValidPreorderStores,
  getValidSamedayStores,
  hasValidStore,
  isAsapValidOperatingHours,
  matchTimeSlotToFulfillmentTime,
  returnFullStore,
  storesAsapEnabled,
  storesAsapOnly,
  trackDateChanged,
  trackOrderDayChanged,
  trackPostCodeChange,
  trackTimeslotChanged
} from '../utils'
import { QUERY_CUSTOMER_DEFAULT_ADDRESS } from 'shop/client/queries/CustomerQueries'
import {
  useShop,
  useConsumerCart,
  useQueryParamsFulfillment,
  useDelivery,
  useNavigatorGeolocation
} from 'shop/hooks'
import { useUTMTracker } from '../trackingUtils'
import { formatStoreTimeslots } from '../TimeSlot/utils'
import { isToday } from 'date-fns'
import Dropdown from 'shop/components/Inputs/Dropdown'
import { isDateAvailable } from '../datePickerUtils'
import { RiMapPinLine as PinIcon } from 'react-icons/ri'
import { initialSelectedOrderDayMapping, OrderDayMapping } from '../utils'
import AddressSelectionNew from '../AddressSelectionNew'
import { findNextAvailableDate } from '../preOrderUtils'
import { DeliveryFormHeading } from './commonStyles'
import DeliveryCalendar from './DeliveryCalendar'
import DeliverySuggestedStore from './DeliverySuggestedStore'
import DeliveryTimeslot from './DeliveryTimeslot'
import ModalWrapper from './ModalWrapper'
import { usePrevious } from 'shop/hooks'
import DeliveryStore from './DeliveryStore'
import DeliveryOrderDay from './DeliveryOrderDay'
import { createAddressString } from 'shop/utils/address'
import {
  findDefaultFulfillment,
  formatFulfillmentWindow
} from 'shop/utils/common'
import { timezoneNow } from 'shop/utils'
import { TfiLocationArrow as NavigateIcon } from 'react-icons/tfi'
import { trackGA4CustomSearchByLocation } from 'tracker/GA/custom'

const DeliveryFormNew = () => {
  const {
    stores,
    currentStore,
    currentLocation,
    setCurrentLocation,
    fulfillmentType,
    fulfillmentTime,
    fulfillmentDate,
    setFulfillmentDate,
    setFulfillmentTime,
    setFulfillmentTimeRange,
    merchant,
    setUseCustomerAddress,
    validStores,
    setCurrentStore,
    loadingStores,
    loadingDates,
    isExtendedHour,
    setIsExtendedHour,
    storeOutOfRange,
    availableDates,
    preOrderOnly,
    isOverlayOpen,
    setIsOverlayOpen,
    address,
    setAddress,
    selectedOrderDay,
    setSelectedOrderDay,
    isEditMode,
    setIsEditMode,
    prevCartId,
    setPrevCartId
  } = useContext(LandingContextNew)

  const { pickup_address_enabled } = merchant

  const {
    orderDayDropdownHandler,
    storeListDropdownHandler,
    calendarInputClickHandler,
    isOrderDayDropdownOpen,
    isStoreListDropdownOpen,
    isCalendarOpen,
    isStoreListDropdownEnabled,
    setIsStoreListDropdownEnabled,
    setIsCalendarOpen,
    handleAddressSelectionModal,
    isAddressSelectionOpen,
    setIsOrderDayDropdownOpen,
    setIsStoreListDropdownOpen,
    handleClickOutsideOrderDayDropdown,
    onClickOutsideDatepicker,
    handleClickOutsideTimeSlotDropdown,
    isTimeSlotDropdownOpen,
    setIsTimeSlotDropdownOpen,
    handleClickOutsideStoreListDropdown,
    showInvalidTimeslotModal,
    setShowInvalidTimeslotModal,
    handleLocationSelect,
    onClickTimeSlotDropdownHandler,
    clearAddressHandler,
    setCurrentStoreHandler,
    validStoresList,
    hideMobileModal,
    closeAddressSelectionModal
  } = useDelivery({
    setCurrentLocation,
    currentLocation,
    setCurrentStore,
    validStores,
    address,
    setAddress,
    setSelectedOrderDay,
    selectedOrderDay,
    setIsEditMode,
    setFulfillmentTime,
    setFulfillmentTimeRange,
    isOverlayOpen,
    setIsOverlayOpen
  })

  const {
    geolocationPermission,
    isLocationServiceEnabled,
    fetchGeolocationPermission,
    getGeolocationPosition,
    geocodeLatLngIntoAddress,
    currentPositionCoords,
    currentPositionAddress
  } = useNavigatorGeolocation()

  const { tracker } = useUTMTracker()
  const { useShopClient, currentPartnerStore } = useShop()
  const client = useShopClient()
  const { cart } = useConsumerCart()
  const { fulfillmentQueryParams } = useQueryParamsFulfillment()
  const FulfillmentTimeStaticTimeStamp = useRef(timezoneNow())
  const queryParamFulfillmentType = fulfillmentQueryParams.fulfillmentType
  const defaultFulfillmentInfo =
    findDefaultFulfillment(
      currentPartnerStore?.orderFulfillmentTypes,
      queryParamFulfillmentType
    ) ?? null
  const oldFulfillmentType = usePrevious(
    cart?.fulfillment.type ||
      defaultFulfillmentInfo?.fulfillmentType.toLowerCase()
  )

  /** Store dropdown input list is shown if there is a fulfillment date, stores are loaded and more than 1 valid stores  */
  const showStoreList: boolean = !!fulfillmentDate && validStores.length > 1

  const timeSlots: TimeSlot[] = formatStoreTimeslots(
    currentStore?.fulfillmentTimeslots || [],
    fulfillmentDate
  )

  const isPickup: boolean = fulfillmentType === 'pickup'

  const isPickupAddressEnabled: boolean = isPickup && !!pickup_address_enabled

  // Resets form when user switches from delivery to pickup
  useEffect(() => {
    // Remove currentLocation/address to recall GVS / save address in ref if user switches back
    if (isPickup && !pickup_address_enabled) {
      setAddress('')
      setCurrentLocation(undefined)
      setIsStoreListDropdownEnabled(false)
    }
    // Sets edit mode false when user switches between fulfillment types to ensure default form behaviour
    !!oldFulfillmentType && setIsEditMode(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentType])

  /** If ASAP is the first timeslot, and there are more than 1, select timeSlots[1], otherwise take timeSlots[0]  */
  const selectFirstTimeslot = useCallback((): void => {
    const cartFulfillmentTimeRange =
      cart && formatFulfillmentWindow(cart?.fulfillment.window, 'p')
    const defaultFulfillmentTimeRange = defaultFulfillmentInfo?.earliestTimeslot
      ? formatFulfillmentWindow(defaultFulfillmentInfo.earliestTimeslot, 'p')
      : null

    if (!!timeSlots.length) {
      // If cart has fulfillment time range, check if it's valid & set it
      if (
        isEditMode &&
        (!!cartFulfillmentTimeRange || !!defaultFulfillmentTimeRange) &&
        !!timeSlots &&
        currentStore
      ) {
        if (cartFulfillmentTimeRange) {
          // Does the prev. selected timeslot exist in the available timeslots?
          const cartTimeSlot = timeSlots.find(
            (ts) => ts.range === cartFulfillmentTimeRange
          )

          if (cartTimeSlot) {
            setFulfillmentTime(cartTimeSlot.value)
            setFulfillmentTimeRange(cartTimeSlot.range)
            setPrevCartId(cart?.id || null)
            return
          }
        } else if (defaultFulfillmentTimeRange) {
          const defaultTimeSlot = timeSlots.find(
            (ts) => ts.range === defaultFulfillmentTimeRange
          )
          if (defaultTimeSlot) {
            setFulfillmentTime(defaultTimeSlot.value)
            setFulfillmentTimeRange(defaultTimeSlot.range)
            return
          }
        }
      }
      const timeslot = timeSlots[0]
      if (timeslot.range === 'ASAP' && timeSlots.length > 1) {
        const nonAsapTimeslot = timeSlots[1]
        setFulfillmentTime(nonAsapTimeslot.value)
        setFulfillmentTimeRange(nonAsapTimeslot.range)
      } else {
        setFulfillmentTime(timeslot.value)
        setFulfillmentTimeRange(timeslot.range)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlots, isEditMode, currentStore])

  useEffect(() => {
    if (localStorage.getItem('customerId') && !currentLocation?.address) {
      const customerId = localStorage.getItem('customerId')

      // If customer is editing cart then set default address to bypass save address modal trigger later
      if (isEditMode) {
        setUseCustomerAddress(true)
        return
      }

      client
        .query({
          query: QUERY_CUSTOMER_DEFAULT_ADDRESS,
          variables: { customerId }
        })
        .then((results) => {
          if (results.data.addresses.length > 0) {
            setUseCustomerAddress(true)
            handleLocationSelect(createAddressString(results.data.addresses[0]))
          }
        })
    }
  }, []) // eslint-disable-line

  // Pre-loads address from utmSource
  useEffect(() => {
    if (tracker && tracker.address) {
      handleLocationSelect(tracker.address as string)
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    fetchGeolocationPermission()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Updates deeply nested order day object disabled statuses */
  function selectedOrderDayMappingStatusReducer(
    selectedOrderDayMapping: OrderDayMapping,
    action: { type: string; payload: any }
  ) {
    switch (action.type) {
      case 'update':
        const orderDayField = action.payload.field
        return {
          ...selectedOrderDayMapping,
          [fulfillmentType]: {
            ...selectedOrderDayMapping[fulfillmentType],
            [orderDayField]: {
              ...selectedOrderDayMapping[fulfillmentType][orderDayField],
              disabled: action.payload.value
            }
          }
        }
      default:
        return selectedOrderDayMapping
    }
  }

  const [selectedOrderDayMapping, dispatch] = useReducer(
    selectedOrderDayMappingStatusReducer,
    initialSelectedOrderDayMapping
  )

  /** If order day is disabled return non-disabled order day */
  const findAvailableOrderDay = useCallback(() => {
    if (
      selectedOrderDayMapping[fulfillmentType] &&
      selectedOrderDayMapping[fulfillmentType][selectedOrderDay]?.disabled
    ) {
      return Object.keys(selectedOrderDayMapping[fulfillmentType]).find(
        (orderDay) => {
          return !selectedOrderDayMapping[fulfillmentType][orderDay].disabled
        }
      )
    } else return null
  }, [selectedOrderDayMapping, fulfillmentType, selectedOrderDay])

  useEffect(() => {
    // Sets order day to non-disabled value When order day mapping / fulfilment type changes
    const availableOrderDay = findAvailableOrderDay()
    availableOrderDay && setSelectedOrderDay(availableOrderDay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderDayMapping, fulfillmentType])

  /** Geocode latLng into address when we can */
  useEffect(() => {
    if (!isLocationServiceEnabled) return

    if (currentPositionCoords) {
      geocodeLatLngIntoAddress(currentPositionCoords)
    } else {
      getGeolocationPosition()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocationServiceEnabled, currentPositionCoords])

  // Set location after first successful geolocation
  useEffect(() => {
    if (
      isLocationServiceEnabled &&
      currentPositionAddress?.formattedAddress &&
      currentPositionCoords &&
      !currentLocation
    ) {
      setCurrentLocationAddress()
      // If the address selection modal is open, close it after setting location
      if (isAddressSelectionOpen) {
        closeAddressSelectionModal()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocationServiceEnabled, currentPositionAddress, currentPositionCoords])

  useEffect(() => {
    if (currentStore && fulfillmentType && stores) {
      const fullCurrentStore = returnFullStore(stores, currentStore)

      if (fullCurrentStore) {
        const { validStores: availableSameDayStores } = getValidSamedayStores(
          stores,
          fulfillmentType,
          new Date(),
          currentLocation
        )

        const { validStores: availableStoresForPreorder } =
          getValidPreorderStores(
            stores,
            fulfillmentType,
            undefined,
            currentLocation
          )

        // purely for readability
        const disabled = true
        const enabled = false

        // Reset order day values
        dispatch({ type: 'update', payload: { field: 'now', value: disabled } })
        dispatch({
          type: 'update',
          payload: { field: 'scheduled', value: enabled }
        })

        // Available same day stores exist and there are available same day asap enabled stores
        if (
          availableSameDayStores.length > 0 &&
          storesAsapEnabled(
            availableSameDayStores,
            fulfillmentType,
            fulfillmentDate
          ) &&
          isAsapValidOperatingHours(fulfillmentType, availableSameDayStores)
        ) {
          const currentStoreIsAsapEnabled = storesAsapEnabled(
            [fullCurrentStore],
            fulfillmentType,
            fulfillmentDate
          )

          // If current store isn't asap enabled, or is asap enabled and out of operating hours, set order day to scheduled
          if (
            !currentStoreIsAsapEnabled ||
            (currentStoreIsAsapEnabled &&
              !isAsapValidOperatingHours(fulfillmentType, [fullCurrentStore]))
          ) {
            setSelectedOrderDay('scheduled')
          }

          // Enable deliver/pickup now as there are same day stores and some are asap enabled
          dispatch({
            type: 'update',
            payload: { field: 'now', value: enabled }
          })

          // If all same day stores are asap only disable scheduled and set order day to now
          if (
            storesAsapOnly(availableSameDayStores) &&
            !availableStoresForPreorder.length
          ) {
            dispatch({
              type: 'update',
              payload: { field: 'scheduled', value: disabled }
            })
            setSelectedOrderDay('now')
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, fulfillmentType, currentLocation])

  useEffect(() => {
    if (
      ((cart && isEditMode && prevCartId !== cart?.id) ||
        !fulfillmentTime ||
        fulfillmentTime === 'immediately' ||
        !matchTimeSlotToFulfillmentTime(timeSlots, fulfillmentTime)) &&
      selectedOrderDay === 'scheduled'
    ) {
      selectFirstTimeslot()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderDay, selectFirstTimeslot, timeSlots, fulfillmentTime])

  // Resets fulfillment time if order day is 'now'
  useEffect(() => {
    if (selectedOrderDay === 'now') {
      setFulfillmentTime('immediately')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderDay])

  // If fulfillment type or fulfillment date is changed show suggested store text (not dropdown)
  useEffect(() => {
    setIsStoreListDropdownEnabled(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentType, fulfillmentDate])

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      if (!date) return
      // If all stores are pre-order only & date is today don't allow user to select today
      if (isToday(date)) {
        if (preOrderOnly) {
          return
        }
        // if selected date is today and current store is asap only then switch user's order day to 'now'
        currentStore && setOrderDayIfAsapOnly(stores, currentStore)
      }

      setIsOverlayOpen(false)
      setTimeout(() => {
        setFulfillmentDate(date)
        setIsCalendarOpen(false)
      }, fadeInOutMs)
      trackDateChanged('modal', date)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setIsOverlayOpen,
      stores,
      currentStore,
      selectedOrderDay,
      preOrderOnly,
      setFulfillmentDate,
      setIsCalendarOpen
    ]
  )

  /** Handles order day value on order day dropdown click */
  const selectedOrderDayHandler = useCallback(
    (orderDayValue: string): void => {
      const itemKeys = Object.keys(selectedOrderDayMapping[fulfillmentType])
      const orderDay = itemKeys.find(
        (itemKey) =>
          selectedOrderDayMapping[fulfillmentType][itemKey] === orderDayValue
      ) as string

      setSelectedOrderDay(orderDay)

      const fullCurrentStore =
        currentStore && returnFullStore(stores, currentStore)
      // If selected order day is 'now'
      if (orderDay === 'now') {
        trackOrderDayChanged('today', 'modal')
        // Set fulfillment date to today when order day is 'now'
        if (!isToday(fulfillmentDate)) {
          setFulfillmentDate(new Date())
        }
        if (fullCurrentStore) {
          const currentStoreIsAsapEnabled = storesAsapEnabled(
            [fullCurrentStore],
            fulfillmentType,
            fulfillmentDate
          )
          // If current store is not asap enabled, or store is asap enabled but not in operating hours, find nearest next available store that is asap enabled & not the current store
          if (
            fullCurrentStore &&
            (!storesAsapEnabled(
              [fullCurrentStore],
              fulfillmentType,
              fulfillmentDate
            ) ||
              (currentStoreIsAsapEnabled &&
                !isAsapValidOperatingHours(fulfillmentType, [
                  fullCurrentStore
                ])))
          ) {
            const { validStores: availableSameDayStores } =
              getValidSamedayStores(
                stores,
                fulfillmentType,
                new Date(),
                currentLocation
              )
            // Find & set next available store that is asap enabled & not the current store
            const nextStoreScheduleToday = findSameDayAsapEnabledStore(
              availableSameDayStores,
              fullCurrentStore,
              fulfillmentType,
              fulfillmentDate
            )

            if (nextStoreScheduleToday) {
              const nextStore = validStores.find(
                (store) => store.name === nextStoreScheduleToday.name
              )
              nextStore && setCurrentStore(nextStore)
            }
          }
        }
      }

      // If selected order day is 'scheduled'
      if (orderDay === 'scheduled') {
        trackOrderDayChanged('future date', 'modal')
        // if current selected store is asap only
        if (fullCurrentStore && storesAsapOnly([fullCurrentStore])) {
          const { validStores: availableSameDayStores } = getValidSamedayStores(
            stores,
            fulfillmentType,
            fulfillmentDate,
            currentLocation
          )
          // if there are no available same day stores or every available same day store is asap only switch to the nearest preorder date
          if (
            !availableSameDayStores.length ||
            storesAsapOnly(availableSameDayStores)
          ) {
            const { validStores: availableStoresForPreorder } =
              getValidPreorderStores(
                stores,
                fulfillmentType,
                undefined,
                currentLocation
              )
            const nearestDate = findNextAvailableDate(
              availableStoresForPreorder,
              fulfillmentType,
              availableDates
            )
            setFulfillmentDate(nearestDate)
          } else {
            // if available same day stores, find & set next available same day non asap only store that is not the current store
            let nextStore: ValidStore | undefined
            const nextStoreScheduleToday =
              findSameDayAsapEnabledNoneAsapOnlyStore(
                availableSameDayStores,
                fullCurrentStore,
                fulfillmentType,
                fulfillmentDate
              )

            // if there is an available same day store that is asap enabled set that as next store
            if (nextStoreScheduleToday) {
              nextStore = validStores.find(
                (store) => store.name === nextStoreScheduleToday.name
              )
              // otherwise set current store as same day valid (same day enabled but none-asap enabled store) as current store
            } else {
              nextStore = validStores.find(
                (store) => store.name !== fullCurrentStore.name
              )
            }
            nextStore && setCurrentStore(nextStore)
          }
        }
      }

      setIsOverlayOpen(false)
      setTimeout(() => {
        setIsOrderDayDropdownOpen(false)
      }, fadeInOutMs)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fulfillmentType,
      selectedOrderDayMapping,
      stores,
      currentStore,
      validStores
    ]
  )

  const handleLocationChange = useCallback(
    (newAddress: string): void => {
      setUseCustomerAddress(false)
      setAddress(newAddress)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUseCustomerAddress]
  )

  const suggestedStoreClickHandler = useCallback(() => {
    if (showStoreList) {
      setIsStoreListDropdownEnabled(true)
      setIsOverlayOpen(true)
      setTimeout(() => {
        setIsStoreListDropdownOpen(true)
      }, fadeInOutMs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStoreList])

  // If order day is scheduled and current store is asap only, set order day to now (deliver/pickup now)
  const setOrderDayIfAsapOnly = (stores: Store[], currentStore: ValidStore) => {
    if (selectedOrderDay === 'scheduled') {
      const fullCurrentStore =
        currentStore && returnFullStore(stores, currentStore)
      if (fullCurrentStore && storesAsapOnly([fullCurrentStore])) {
        setSelectedOrderDay('now')
      }
    }
  }

  // If current store changes & fulfillment date is today, check if store is asap only to change order day to 'now'
  useEffect(() => {
    currentStore &&
      isToday(fulfillmentDate) &&
      setOrderDayIfAsapOnly(stores, currentStore)
  }, [currentStore]) // eslint-disable-line

  const selectedTimeSlotHandler = useCallback(
    (selectedTimeSlotDetails: TimeSlot) => {
      setIsOverlayOpen(false)

      if (selectedTimeSlotDetails) {
        const { range, value, extended } = selectedTimeSlotDetails
        setFulfillmentTime(value)
        setFulfillmentTimeRange(range)
        setIsExtendedHour(!!extended)
        trackTimeslotChanged(range, 'modal')
        // If asap and fulfillment date is today, set order day to 'now' (deliver/pickup now)
        if (value === 'immediately' && isToday(fulfillmentDate)) {
          setSelectedOrderDay('now')
        }
        setTimeout(() => {
          setIsTimeSlotDropdownOpen(false)
        }, fadeInOutMs)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      timeSlots,
      fulfillmentDate,
      setFulfillmentTime,
      setFulfillmentTimeRange,
      setIsExtendedHour
    ]
  )

  /** If location service is enabled & we have geolocated users position, track event & set state with address values */
  const setCurrentLocationAddress = () => {
    if (currentPositionAddress?.formattedAddress && currentPositionCoords) {
      const { addressComponents, formattedAddress } = currentPositionAddress
      trackGA4CustomSearchByLocation('slerpGA4Tracker')
      trackGA4CustomSearchByLocation('merchantGA4Tracker')
      trackPostCodeChange('modal')
      setCurrentLocation({
        latLng: currentPositionCoords,
        addressComponents: addressComponents,
        address: formattedAddress
      })
      setAddress(formattedAddress)
    }
  }

  /** Handles user click to get current location for address input / prompt user for permission */
  const handleGetLocationClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (
      isLocationServiceEnabled &&
      currentPositionAddress?.formattedAddress &&
      currentPositionCoords
    ) {
      setCurrentLocationAddress()
      // If clicked from AddressSelectionNew overlay, close the modal without re-geocoding
      if (isAddressSelectionOpen) {
        closeAddressSelectionModal()
      }
      return
    }
    // trigger enable pop up from 'denied' or 'prompt'
    getGeolocationPosition()
  }

  const datepicker = (): JSX.Element => {
    return (
      <>
        <DatePickerContainer>
          {isCalendarOpen && showDatepicker && (
            <FulfillmentDateContainer newFulfillmentModal={true}>
              <FulfillmentDatePicker
                selected={fulfillmentDate}
                onChange={handleDateChange}
                minDate={new Date()}
                filterDate={(date: Date) =>
                  isDateAvailable(availableDates, date, fulfillmentType)
                }
                placeholderText='Date'
                dateFormat='dd/MM/yyyy'
                disabled={
                  loadingStores[fulfillmentType] ||
                  loadingDates[fulfillmentType]
                }
                newFulfillmentModal={true}
              />
            </FulfillmentDateContainer>
          )}
        </DatePickerContainer>
      </>
    )
  }

  const datesOrStoresLoading = useMemo(
    () => loadingDates[fulfillmentType] || loadingStores[fulfillmentType],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingDates[fulfillmentType], loadingStores[fulfillmentType]]
  )

  const showDatepicker: boolean =
    !datesOrStoresLoading && !!validStores.length && !!fulfillmentDate
  const deliveryDateTimeSectionLoading =
    (selectedOrderDay === 'scheduled' && datesOrStoresLoading) ||
    !fulfillmentTime

  const showTimeslotsSection =
    selectedOrderDay === 'scheduled' &&
    (deliveryDateTimeSectionLoading || (showDatepicker && timeSlots.length > 1))

  const showSuggestedStore = !isStoreListDropdownEnabled

  const showStoreListDropdown =
    isStoreListDropdownEnabled && !!currentStore && !!validStoresList.length

  const showAddressSelection: boolean =
    fulfillmentType === 'delivery' || isPickupAddressEnabled

  const showInvalidStoresMessage = !datesOrStoresLoading && !validStores.length

  return (
    <>
      {showAddressSelection && (
        <>
          <DeliveryFormHeading>
            <span>Address or postcode</span>
          </DeliveryFormHeading>
          <Dropdown
            testId='address-selection-input'
            textContent={address}
            textFallback={'e.g. N3 1LJ'}
            hasSelected={!!address}
            IconLeft={
              <>
                <PinIcon />
              </>
            }
            IconRight={
              geolocationPermission === 'denied' || !!address ? undefined : (
                <NavigateIcon
                  onClick={handleGetLocationClick}
                  style={{ fill: 'black', transform: 'scale(-1,1)' }}
                />
              )
            }
            isOpen={isAddressSelectionOpen}
            onClick={handleAddressSelectionModal}
            onClear={clearAddressHandler}
            canEdit={true}
          >
            <ModalWrapper
              onClose={handleAddressSelectionModal}
              scrollBehaviour={'overflow'}
              isOpen={isOverlayOpen}
              hideMobileModal={hideMobileModal}
            >
              <AddressSelectionNew
                onChange={handleLocationChange}
                onSelect={handleLocationSelect}
                address={address}
                onClose={handleAddressSelectionModal}
                onLocationClick={handleGetLocationClick}
                geolocationPermission={geolocationPermission}
              />
            </ModalWrapper>
          </Dropdown>
        </>
      )}
      {!showInvalidStoresMessage && (
        <DeliveryOrderDay
          fulfillmentType={fulfillmentType}
          handleClickOutsideOrderDayDropdown={
            handleClickOutsideOrderDayDropdown
          }
          isOrderDayDropdownOpen={isOrderDayDropdownOpen}
          isOverlayOpen={isOverlayOpen}
          loadingStores={datesOrStoresLoading}
          orderDayDropdownHandler={orderDayDropdownHandler}
          selectedOrderDay={selectedOrderDay}
          selectedOrderDayHandler={selectedOrderDayHandler}
          selectedOrderDayMapping={selectedOrderDayMapping}
        />
      )}
      {selectedOrderDay === 'scheduled' && !showInvalidStoresMessage && (
        <CalendarTimeSlotContainer
          timeslotIsEditable={dropdownIsEditable(timeSlots)}
        >
          <DeliveryCalendar
            fulfillmentDate={fulfillmentDate}
            fulfillmentTime={fulfillmentTime}
            fulfillmentType={fulfillmentType}
            isCalendarOpen={isCalendarOpen}
            calendarInputClickHandler={calendarInputClickHandler}
            availableDates={availableDates}
            isOverlayOpen={isOverlayOpen}
            onClickOutsideDatepicker={onClickOutsideDatepicker}
            timeSlots={timeSlots}
            datepicker={datepicker}
            isLoading={deliveryDateTimeSectionLoading}
          />
          {showTimeslotsSection && (
            <div>
              <DeliveryTimeslot
                fulfillmentTime={fulfillmentTime}
                isTimeSlotDropdownOpen={isTimeSlotDropdownOpen}
                onClickTimeSlotDropdownHandler={onClickTimeSlotDropdownHandler}
                timeSlots={timeSlots}
                selectedTimeSlotHandler={selectedTimeSlotHandler}
                isOverlayOpen={isOverlayOpen}
                handleClickOutsideTimeSlotDropdown={
                  handleClickOutsideTimeSlotDropdown
                }
                isExtendedHour={isExtendedHour}
                isLoading={deliveryDateTimeSectionLoading}
              />
            </div>
          )}
          <InvalidTimeSlot
            showModal={showInvalidTimeslotModal}
            onClose={() => setShowInvalidTimeslotModal(false)}
          />
        </CalendarTimeSlotContainer>
      )}
      {showSuggestedStore && !showInvalidStoresMessage && (
        <DeliverySuggestedStore
          fulfillmentType={fulfillmentType}
          currentStore={currentStore}
          suggestedStoreClickHandler={suggestedStoreClickHandler}
          validStoresList={validStoresList}
          isLoading={datesOrStoresLoading}
        />
      )}
      {showStoreListDropdown && (
        <DeliveryStore
          validStoresList={validStoresList}
          currentStore={currentStore}
          storeListDropdownHandler={storeListDropdownHandler}
          isStoreListDropdownOpen={isStoreListDropdownOpen}
          isOverlayOpen={isOverlayOpen}
          handleClickOutsideStoreListDropdown={
            handleClickOutsideStoreListDropdown
          }
          isPickup={isPickup}
          setCurrentStoreHandler={setCurrentStoreHandler}
          validStores={validStores}
          fulfillmentDate={fulfillmentDate}
          fulfillmentTimeStaticTimeStamp={
            FulfillmentTimeStaticTimeStamp.current
          }
        />
      )}
      {showInvalidStoresMessage && (
        <Message>
          {getInvalidStoresMessage(
            fulfillmentDate,
            storeOutOfRange,
            !!hasValidStore(stores, 'pickup'),
            true
          )}
        </Message>
      )}
    </>
  )
}

const Message = styled.p<StyledHTMLElement>(({ theme }) => ({
  margin: '16px 0 0',
  fontSize: theme.fontSizes[2],
  fontWeight: 500,
  textTransform: 'none',
  color: '#FF4D4F'
}))

const DatePickerContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    zIndex: 101
  })
)

const CalendarTimeSlotContainer = styled.div<
  StyledHTMLElement & { timeslotIsEditable?: boolean }
>(({ timeslotIsEditable = true }) => ({
  display: 'flex',
  height: '50px',
  position: 'relative',
  marginTop: '16px',
  columnGap: '5%',
  ' > div:nth-of-type(1)': {
    width: timeslotIsEditable ? '45%' : '100%'
  },
  '> div:nth-of-type(2)': {
    width: '50%'
  }
}))

export default DeliveryFormNew
